<script lang='ts'>
    import * as Dialog from "$lib/components/ui/dialog/index.js";
    import * as ToggleGroup from "$lib/components/ui/toggle-group";
    import { addDays, format, startOfDay } from "date-fns";
    import { BannerHeader } from "../Common";
    import { Separator } from "../lib/components/ui/separator";
    import { announcementTagLabels, convertToUpdates, newAnnouncement, newUpdate, type AnnouncementTags, type XertUpdates } from "./announcements";
    import Button from "@/lib/components/ui/button/button.svelte";
    import { cn } from "@/lib/utils";
    import Badge from "@/lib/components/ui/badge/badge.svelte";
    import { Input } from "$lib/components/ui/input/index.js";
    import DialogContent from "@/lib/components/ui/dialog/dialog-content.svelte";
    import Label from "@/lib/components/ui/label/label.svelte";
    import { Textarea } from "$lib/components/ui/textarea/index.js";
    import Checkbox from "@/lib/components/ui/checkbox/checkbox.svelte";
    import { baseRequest, getRequest } from "@/Util/utils";
    import route from "@/Util/routes";
    import { LocalDate } from "@/time";
    import * as Popover from "$lib/components/ui/popover/index.js";

    export let mostRecentUpdate: string
    export let isAdmin: boolean
    const mostRecentUpdateDate = mostRecentUpdate ? LocalDate.toDate(mostRecentUpdate) : startOfDay(new Date())
    const storageKey = "last_update"
    const lastKnownUpdateString = JSON.parse(localStorage.getItem(storageKey))
    const lastKnownUpdate = lastKnownUpdateString && LocalDate.toDate(lastKnownUpdateString)

    let updatesBackend: XertUpdates[]
    let filteredUpdates: XertUpdates[]
    type FilterTags = AnnouncementTags | "all"
    let filterTag: FilterTags = "all"
    let updatesPromise: Promise<any>

    function openChange(open: boolean) {
        if(!open) localStorage.setItem(storageKey, JSON.stringify(mostRecentUpdate ?? format(mostRecentUpdateDate, "yyyy-MM-dd")))
        if(open) {
            updatesPromise = getUpdates()
            updatesPromise.then(
                (response) => {
                    updatesBackend = filteredUpdates = convertToUpdates(response)
                }
            )
        }
    }

    export function openModal() {
        open = true
        openChange(open) // need to call manually because not opening onOpenChange
    }

    function openOnLoad() {
        if(!lastKnownUpdate || (lastKnownUpdate < mostRecentUpdateDate)) {
            openChange(true)
            return true
        }
        return false
    }
 
    let open = openOnLoad()

    async function getUpdates() {
        return window.fetch(
            route('UserController@getUpdates'),
            {
                ...getRequest()
            }
        ).then(r=>r.json())
    }


    function filterUpdates(tag: FilterTags) {
        if(tag === "all" || !tag) {
            filteredUpdates = updatesBackend
        } else {
            filteredUpdates = updatesBackend.filter((update) => {
                for (const announcement of update.announcements) {
                    if (announcement?.tags?.includes(tag)) return true
                }
                return false
            })
        }
    }

    /*-------------------- ADMIN ONLY:  Edit Dialog --------------------*/
    let openEdit = false
    let editForm: HTMLFormElement
    let updateDate: string
    let updateToEdit: XertUpdates
    let editExisting: boolean

    function openEditDialog(update: XertUpdates) {
        updateToEdit = {
            ...update,
            announcements: update.announcements.map((a) => {return {...a}})
        }
        updateDate = LocalDate.format(update.date) // Need variable for input bind
        editExisting = !!updateToEdit.id  
        openEdit = true
    }

    function updateTags(tags: string[], i: number) {
        updateToEdit.announcements[i].tags = tags as AnnouncementTags[]
    }

    async function submitUpdate() {
        let formData = new FormData(editForm)
        updateToEdit.announcements.forEach((announcement) => {
            formData.append("announcements_tags[]", JSON.stringify(announcement.tags))
        })
        formData.append('update_hidden', JSON.stringify(updateToEdit.hidden))
        formData.append('id', updateToEdit.id)

        window.fetch(
            route('AdminController@postUpdate'),
            {
                ...baseRequest(),
                method: 'POST',
                body: formData,
            }, 
        ).then(r=>r.json()).then((response) => {
            updatesBackend = convertToUpdates(response)
            filterUpdates(filterTag)
            openEdit = false
        })
    }

    async function deleteUpdate(id: string) {
        window.fetch(
            route('AdminController@deleteUpdate'),
            {
                ...getRequest(),
                method: 'POST',
                body: JSON.stringify({
                    id
                }),
            }, 
        ).then(r=>r.json()).then((response) => {
            updatesBackend = convertToUpdates(response)
            filterUpdates(filterTag)
        })
    }
</script>

<Dialog.Root preventScroll portal="#shadcn-ui-dialog-target" bind:open openFocus={"#whats-new-banner"} onOpenChange={openChange}>
    <Dialog.Content class="p-0 max-w-4xl focus:outline-none [&_svg[aria-label='cross_2,']]:text-white [&_button[data-dialog-close]]:z-20 [&_button[data-dialog-close]]:opacity-100 translate-y-0 lg:top-2 top-0" id="whats-new-banner">
        <BannerHeader class="h-[100dvh] lg:h-[97dvh]" bodyClass="md:px-4 md:pb-4 lg:px-4 lg:pb-4">

            <div class="flex flex-col gap-4 justify-center items-center px-4 pb-4 pt-8 md:px-8 md:pb-8 text-white" slot="header">
                <div class="flex gap-4 items-center">
                    <i class="fa-light fa-newspaper text-5xl"/>
                    <span class="font-bold text-3xl"> Xert Newsfeed </span>
                </div>
                <ToggleGroup.Root class="gap-2" bind:value={filterTag} variant="outline" type="single" onValueChange={filterUpdates}>
                    <ToggleGroup.Item value="all">All</ToggleGroup.Item>
                    <ToggleGroup.Item value="feature">Features</ToggleGroup.Item>
                    <ToggleGroup.Item value="new_user">New Users</ToggleGroup.Item>
                    <ToggleGroup.Item value="blog">Blogs</ToggleGroup.Item>
                    <ToggleGroup.Item class="hidden sm:block" value="podcast">Podcasts</ToggleGroup.Item>
                    <ToggleGroup.Item class="hidden sm:block" value="video">Videos</ToggleGroup.Item>
                    <ToggleGroup.Item class="hidden sm:block" value="media">Media Coverage</ToggleGroup.Item>
                </ToggleGroup.Root>
            </div>

            <div class="header-background w-full h-full rounded-t" slot="banner"/>

            <svelte:fragment slot="body">
                {#await updatesPromise}
                    <div class="flex bg-white shadow-md rounded py-4 sm:py-6 h-full place-items-center">
                        <i class="fa-light fa-newspaper text-9xl animate-pulse flex-1 text-center"/>
                    </div>
                {:then} 
                    <div class="flex flex-col bg-white shadow-md rounded flex-1 text-base py-4 sm:py-6 max-h-full">
                        {#if isAdmin}<Button class="mb-4 mx-4" on:click={() => openEditDialog(newUpdate)}>Add New Update</Button>{/if}
                        <div class="overflow-auto">
                            {#each filteredUpdates as update}
                                {@const multipleAnnouncements = update.announcements.length > 1}
                                {#if isAdmin || !update.hidden}
                                    <Separator class="mx-3 sm:mx-5 w-auto h-3 rounded mb-4 first:mt-0 mt-8 bg-xerteal-400/20 last:hidden"/>
                                    <div class="px-4 sm:px-6">
    
                                        {#if isAdmin && update.hidden}<div class="mb-2 text-red-600">This update is currently hidden from view.</div>{/if}
                                        <div class="mb-2 flex items-center gap-3">       
                                            <div class="text-muted-foreground leading-none text-lg">{format(update.date, 'do MMMM yyyy')}</div>
                                            <div class="bg-yellow-500 text-white rounded font-bold text-center px-3" class:hidden={lastKnownUpdate >= update.date || addDays(update.date, 30) < new Date()}>New!</div>
                                            {#if isAdmin}
                                                <Button size="icon" variant="ghost" on:click={() => openEditDialog(update)}><i class="fa-solid fa-edit"/></Button>
                                                <Popover.Root>
                                                    <Popover.Trigger asChild let:builder>
                                                      <Button class="ml-auto" builders={[builder]} variant="destructive" size="sm">Delete</Button>
                                                    </Popover.Trigger>
                                                    <Popover.Content>
                                                        <div class="grid grid-cols-2 gap-3">
                                                            <div class="col-span-2 text-center">Are you sure you want to delete this update ?</div>
                                                            <Popover.Close asChild let:builder>
                                                                <Button builders={[builder]} size="sm">No</Button>
                                                            </Popover.Close>
                                                            <Popover.Close asChild let:builder>
                                                                <Button builders={[builder]} variant="outline" size="sm" on:click={() => deleteUpdate(update.id)}>Yes</Button>
                                                            </Popover.Close>
                                                        </div>
                                                    </Popover.Content>
                                                </Popover.Root>
                                                
                                            {/if}
                                        </div>
                                        {#if update.title} <div class="leading-none font-semibold text-xl mb-3">{update.title}</div>{/if}
                                        {#if update.description}<div class="mb-6">{update.description}</div>{/if}
                                                                
                                        <div>
                                            {#each update.announcements as announcement}
                                                <Separator class={cn("mb-4 first:mt-0 mt-7", {"hidden": !multipleAnnouncements})}/>
                                                <div class="flex flex-wrap items-center gap-2">
                                                    <div class={cn("font-semibold text-xl leading-none", {"text-lg leading-none font-medium": multipleAnnouncements})}>{announcement.title}</div> 
                                                    {#if announcement.tags}
                                                        <div class="flex flex-wrap gap-1">
                                                            {#each announcement.tags as tag}
                                                                <Badge variant="outline">{announcementTagLabels[tag]}</Badge>
                                                            {/each}
                                                        </div>
                                                    {/if}
                                                </div>
                                                <div class="relative flex flex-col sm:flex-row gap-x-4 gap-y-3 mt-4">
                                                    <img class="object-cover rounded w-52 aspect-video shadow grow-0 h-max" alt={announcement.imageAlt} src={announcement.imageUrl}/>
                                                    <div class="flex flex-col gap-3">
                                                        <div>{announcement.description}</div>
                                                        <Button class="self-start" size="sm" variant="secondary" href={announcement.url} target="_blank">{announcement.buttonText}</Button>
                                                    </div>
                                                </div>
                                            {/each}
                                        </div>
    
                                    </div>
                                {/if}
                            {/each}   
                        </div>
                    </div>  
                {/await}
            </svelte:fragment>        
        </BannerHeader>
    </Dialog.Content>
</Dialog.Root>

<Dialog.Root preventScroll portal="#shadcn-ui-dialog-target" bind:open={openEdit}>
    <DialogContent class="h-[100dvh] lg:h-[97dvh] max-w-2xl">
        <form class="overflow-auto px-2" bind:this={editForm}>
            <div class="mb-4 space-y-1">
                <div class="text-xl font-bold border-b">{editExisting ? "Edit" : "New"} Update</div>
                {#if updateToEdit.id}<div>Update Id: {updateToEdit.id}</div>{/if}
            </div>
            <div class="space-y-4">
                <div class="space-y-2">
                    <Label class="mb-0" for="update-title">Title</Label>
                    <Input bind:value={updateToEdit.title} id="update-title" type="text" name="update_title"/>
                </div>
                <div class="space-y-2">
                    <Label class="mb-0" for="update-description">Description</Label>
                    <Textarea bind:value={updateToEdit.description} id="update-description" name="update_description"/>
                </div>
                <div class="space-y-2">
                    <Label class="mb-0" for="update-date">Date</Label>
                    <Input bind:value={updateDate} id="update-date" type="date" name="update_date"/>
                </div>
                <div class="flex gap-2 items-center">
                    <Checkbox bind:checked={updateToEdit.hidden} id="update-hidden" name="update_hidden"/>
                    <Label class="mb-0" for="update-hidden">Hidden</Label>  
                </div>
            </div>
            <div class="font-bold text-lg mt-4 mb-2">Announcements</div>
            <div class="border p-6 rounded">
                {#each updateToEdit.announcements as announcement, i (i)}
                    <div class="space-y-4">
                        <div class="flex justify-between">
                            <span class="text-muted-foreground font-medium">Announcement {i}</span>
                            <Button size="sm" variant="outline" on:click={() => {updateToEdit.announcements.splice(i, 1); updateToEdit=updateToEdit}}><i class="fa-solid fa-minus"/></Button>
                        </div>
                        <div class="space-y-2">
                            <Label class="mb-0" for="announcements-title-{i}">Title</Label>
                            <Input bind:value={announcement.title} id="announcements-title-{i}" type="text" name="announcements_title[]"/>
                        </div>
                        <div class="space-y-2">
                            <Label class="mb-0" for="announcements-description-{i}">Description</Label>
                            <Textarea bind:value={announcement.description} id="announcements-description-{i}" name="announcements_description[]"/>
                        </div>
                        <div class="space-y-2">
                            <Label class="mb-0" for="announcements-image-{i}">Image</Label>
                            <div class="border px-3 py-2 rounded space-y-2">
                                
                                <Label class="mb-0 font-normal" for="announcements-image-{i}">File</Label>
                                <Input id="announcements-image-{i}" type="file" name="announcements_image[]"/>
                                <Label class="mb-0 font-normal" for="announcements-image-url-{i}">Url</Label>
                                <Input class="mb-4"  bind:value={announcement.imageUrl} id="announcements-image-url-{i}" type="text" name="announcements_image_urls[]"/>
                                <div class="italic"> If there is a file it will be override any url. For existing updates the url points to the original image.</div>
                            </div>
                        </div>
                        <div class="space-y-2">
                            
                        </div>
                        <div class="space-y-2">
                            <Label class="mb-0" for="announcements-image-alt-{i}">Image Alt</Label>
                            <Input bind:value={announcement.imageAlt} id="announcements-image-alt-{i}" type="text" name="announcements_image_alt[]"/>
                        </div>
                        <div class="space-y-2">
                            <Label class="mb-0" for="announcements-button-text-{i}">Button Text</Label>
                            <Input bind:value={announcement.buttonText} id="announcements-button-text-{i}" type="text" name="announcements_button_text[]"/>
                        </div>
                        <div class="space-y-2">
                            <Label class="mb-0" for="announcements-url-{i}">Button Url</Label>
                            <Input bind:value={announcement.url} id="announcements-url-{i}" type="text" name="announcements_url[]"/>
                        </div>
                        <div class="space-y-2">
                            <Label class="mb-0">Tags</Label>
                            <ToggleGroup.Root class="gap-1 flex-wrap" value={updateToEdit.announcements[i].tags} onValueChange={(values) => updateTags(values, i)} variant="outline" type="multiple">
                                <ToggleGroup.Item value="feature">Features</ToggleGroup.Item>
                                <ToggleGroup.Item value="new_user">New Users</ToggleGroup.Item>
                                <ToggleGroup.Item value="blog">Blogs</ToggleGroup.Item>
                                <ToggleGroup.Item class="hidden sm:block" value="podcast">Podcasts</ToggleGroup.Item>
                                <ToggleGroup.Item class="hidden sm:block" value="video">Videos</ToggleGroup.Item>
                                <ToggleGroup.Item class="hidden sm:block" value="media">Media Coverage</ToggleGroup.Item>
                            </ToggleGroup.Root>
                        </div>
                    </div>
                    <Separator class="last:hidden mb-6 mt-3 h-2 rounded"/>
                {/each}
                <Button class="ml-auto" size="sm" on:click={() => {updateToEdit.announcements.push({...newAnnouncement}); updateToEdit=updateToEdit;}}><i class="fa-solid fa-plus"/></Button>
            </div>
            <Button class="mt-4" size="sm" on:click={submitUpdate}>Submit Xert Update</Button>
        </form>
    </DialogContent>
</Dialog.Root>

<style lang="scss">

    .header-background {
        background: linear-gradient(0deg, rgba(var(--rgb-xert-teal), 0.75) 0%, rgba(var(--rgb-xert-teal), 0.75) 100%), url("../../assets/images/xfai/Road cyclist riding through a surreal, multi-dimensional universe.webp");
        background-position: right 38% top 43%;
    }

</style>
